import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sparkles,
  Plus,
  FolderOpen,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
  Image as ImageIcon,
  Copy,
  X,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { CreativeGroup, Creative } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useSearch } from "wouter";

export default function CreateAds() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const searchString = useSearch();

  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<CreativeGroup | null>(null);
  const [groupName, setGroupName] = useState("");

  const cloneParams = useMemo(() => {
    const params = new URLSearchParams(searchString);
    const clone = params.get("clone");
    const clones = params.get("clones");
    if (clone) return { type: "single" as const, clone };
    if (clones) {
      try {
        const urls: string[] = JSON.parse(clones);
        return { type: "multi" as const, clones: urls };
      } catch { return null; }
    }
    return null;
  }, [searchString]);

  const hasCloneIntent = !!cloneParams;

  const clearCloneParams = () => {
    navigate("/create-ads", { replace: true });
  };

  const buildGroupUrl = (groupId: number) => {
    if (cloneParams?.type === "single") {
      return `/create-ads/group/${groupId}?clone=${encodeURIComponent(cloneParams.clone)}`;
    }
    if (cloneParams?.type === "multi") {
      return `/create-ads/group/${groupId}?clones=${encodeURIComponent(JSON.stringify(cloneParams.clones))}`;
    }
    return `/create-ads/group/${groupId}`;
  };

  const { data: groups = [], isLoading } = useQuery<CreativeGroup[]>({
    queryKey: ["/api/creative-groups"],
  });

  const { data: allCreatives = [] } = useQuery<Creative[]>({
    queryKey: ["/api/creatives"],
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/creative-groups", { name });
      return res.json();
    },
    onSuccess: (group: CreativeGroup) => {
      queryClient.invalidateQueries({ queryKey: ["/api/creative-groups"] });
      setCreateOpen(false);
      setGroupName("");
      navigate(buildGroupUrl(group.id));
    },
    onError: (err: any) => {
      toast({ title: "Failed to create group", description: err.message, variant: "destructive" });
    },
  });

  const renameMutation = useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const res = await apiRequest("PATCH", `/api/creative-groups/${id}`, { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/creative-groups"] });
      setEditGroup(null);
      setGroupName("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/creative-groups/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/creative-groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/creatives"] });
      toast({ title: "Group deleted" });
    },
  });

  const getGroupCreatives = (groupId: number) =>
    allCreatives.filter((c) => c.groupId === groupId);

  const getGroupCover = (groupId: number) => {
    const groupCreatives = getGroupCreatives(groupId);
    const completed = groupCreatives.find((c) => c.status === "completed" && c.imageUrl);
    return completed?.imageUrl || null;
  };

  const getGroupStats = (groupId: number) => {
    const groupCreatives = getGroupCreatives(groupId);
    return {
      total: groupCreatives.length,
      completed: groupCreatives.filter((c) => c.status === "completed").length,
      generating: groupCreatives.filter((c) => c.status === "generating" || c.status === "pending").length,
    };
  };

  const cloneCount = cloneParams?.type === "multi" ? cloneParams.clones.length : cloneParams?.type === "single" ? 1 : 0;

  return (
    <Layout>
      <div className="flex flex-col h-full bg-gray-50">
        <div className="border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 data-testid="text-page-title" className="text-xl font-bold text-gray-900">
                Create Ads
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Organize your AI-generated ad creatives into projects.
              </p>
            </div>
            <Button
              data-testid="button-create-group"
              className="bg-indigo-600 hover:bg-indigo-700"
              size="sm"
              onClick={() => { setGroupName(""); setCreateOpen(true); }}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              New Project
            </Button>
          </div>
        </div>

        {hasCloneIntent && (
          <div className="bg-indigo-50 border-b border-indigo-100 px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Copy className="h-4 w-4 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-indigo-900">
                    {cloneCount === 1 ? "Clone template ready" : `${cloneCount} templates ready to clone`}
                  </p>
                  <p className="text-xs text-indigo-600">
                    Select a project below or create a new one to start generating.
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100"
                onClick={clearCloneParams}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto px-6 py-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="h-20 w-20 bg-indigo-50 rounded-2xl flex items-center justify-center mb-4">
                <Sparkles className="h-10 w-10 text-indigo-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {hasCloneIntent ? "Create a project first" : "No projects yet"}
              </h3>
              <p className="text-sm text-gray-500 max-w-md mb-4">
                {hasCloneIntent
                  ? "You need a project to hold your cloned ads. Create one to get started."
                  : "Create a project to start generating AI-powered ad creatives. Each project keeps your ads organized."
                }
              </p>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => { setGroupName(""); setCreateOpen(true); }}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {hasCloneIntent ? "Create Project & Clone" : "Create Your First Project"}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <button
                data-testid="button-create-group-card"
                className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center h-60 hover:border-indigo-400 hover:bg-indigo-50/50 transition-all group"
                onClick={() => { setGroupName(""); setCreateOpen(true); }}
              >
                <div className="h-12 w-12 rounded-full bg-gray-100 group-hover:bg-indigo-100 flex items-center justify-center mb-3 transition-colors">
                  <Plus className="h-6 w-6 text-gray-400 group-hover:text-indigo-500" />
                </div>
                <span className="text-sm font-medium text-gray-500 group-hover:text-indigo-600">New Project</span>
              </button>

              {groups.map((group) => {
                const cover = getGroupCover(group.id);
                const stats = getGroupStats(group.id);
                return (
                  <div
                    key={group.id}
                    data-testid={`card-group-${group.id}`}
                    className={`bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-all cursor-pointer group relative ${
                      hasCloneIntent ? "border-indigo-200 hover:border-indigo-400 ring-1 ring-indigo-100" : "border-gray-200"
                    }`}
                    onClick={() => navigate(buildGroupUrl(group.id))}
                  >
                    <div className="h-40 bg-gray-100 relative overflow-hidden">
                      {cover ? (
                        <img src={cover} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <FolderOpen className="h-12 w-12 text-gray-300" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />

                      {!hasCloneIntent && (
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="secondary" size="icon" className="h-7 w-7 bg-white/90 hover:bg-white shadow-sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                setEditGroup(group);
                                setGroupName(group.name);
                              }}>
                                <Pencil className="h-4 w-4 mr-2" /> Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm(`Delete "${group.name}" and all its ads?`)) {
                                    deleteMutation.mutate(group.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}

                      {hasCloneIntent && (
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-indigo-500/90 text-white border-0 text-[10px]">
                            <Copy className="h-2.5 w-2.5 mr-1" />
                            Clone here
                          </Badge>
                        </div>
                      )}

                      {stats.generating > 0 && (
                        <Badge className="absolute bottom-2 right-2 bg-amber-500/90 text-white border-0 text-[10px]">
                          <Loader2 className="h-2.5 w-2.5 mr-1 animate-spin" />
                          {stats.generating} generating
                        </Badge>
                      )}
                    </div>

                    <div className="p-3">
                      <h3 className="font-semibold text-sm text-gray-900 truncate">{group.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          <ImageIcon className="h-3 w-3 inline mr-0.5" />
                          {stats.completed} ad{stats.completed !== 1 ? "s" : ""}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {new Date(group.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{hasCloneIntent ? "Create project for cloned ads" : "New Project"}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              data-testid="input-group-name"
              placeholder="e.g., Summer Campaign, Product Launch..."
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && groupName.trim()) createMutation.mutate(groupName.trim());
              }}
              autoFocus
            />
            {hasCloneIntent && (
              <p className="text-xs text-gray-500 mt-2">
                {cloneCount === 1
                  ? "Your cloned template will be loaded in this project."
                  : `${cloneCount} templates will be loaded in this project.`
                }
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              data-testid="button-confirm-create"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={!groupName.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate(groupName.trim())}
            >
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {hasCloneIntent ? "Create & Clone" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editGroup} onOpenChange={(open) => { if (!open) setEditGroup(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Project</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              data-testid="input-rename-group"
              placeholder="Project name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && groupName.trim() && editGroup) {
                  renameMutation.mutate({ id: editGroup.id, name: groupName.trim() });
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGroup(null)}>Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={!groupName.trim() || renameMutation.isPending}
              onClick={() => editGroup && renameMutation.mutate({ id: editGroup.id, name: groupName.trim() })}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
